import React, { useEffect, useRef, useState, useMemo } from 'react'
import styles from './styles.module.scss'
import { ReactComponent as StomatologyIcon } from '../../../../../../assets/icons/stomatology.svg'
import { ReactComponent as PulmonologyIcon } from '../../../../../../assets/icons/pulmonology.svg'
import { ReactComponent as AddIcon } from '../../../../../../assets/icons/plus-ico.svg'

export default function index({ 
  t, 
  selectedPatient, 
  patients, 
  setSelectedPatient, 
  addNewPatientToggle, 
  editing, 
  isNew, 
  disablePatientChange,
  initialOpen = false,
  setMenuOpen = null,
  selectedCategory
}) {
  const [searchInput, setSearchInput] = useState('')
  const [opMenu, setOpMenu] = useState(initialOpen)
  const [searchCategory, setSearchCategory] = useState(selectedCategory ? [selectedCategory] : ['stomatology', 'pulmonology'])
  const menuRef = useRef(null)

  // Update local menu state when external control changes
  useEffect(() => {
    if (initialOpen !== undefined && setMenuOpen) {
      setOpMenu(initialOpen);
    }
  }, [initialOpen]);

  // Update searchCategory if selectedCategory changes
  useEffect(() => {
    if (selectedCategory) {
      setSearchCategory([selectedCategory]);
    }
  }, [selectedCategory]);

  // Sync menu state with parent component if setMenuOpen is provided
  useEffect(() => {
    if (setMenuOpen) {
      setMenuOpen(opMenu);
    }
  }, [opMenu, setMenuOpen]);

  const handleClickOutside = (e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setOpMenu(false);
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // PERFORMANCE OPTIMIZATION: Direct filtering with immediate response
  const filteredOperations = useMemo(() => {
    if (!patients) return [];
    
    const searchInputLower = searchInput.toLowerCase();
    const hasSearchTerm = searchInputLower.length > 0;
    
    const filtered = patients.filter((pt) => {
      const opNameLower = pt.full_name?.toLowerCase() || '';
      const opCategoryLower = pt.category?.toLowerCase() || '';
      
      // Category filter
      const categoryMatch = searchCategory?.includes(opCategoryLower) || searchCategory.length === 0;
      
      // Name filter (only if there's a search term)
      const nameMatch = !hasSearchTerm || opNameLower.includes(searchInputLower);
      
      return categoryMatch && nameMatch;
    });
    
    // PERFORMANCE: Limit results to prevent UI lag with large datasets
    return filtered.slice(0, 50); // Show max 50 results
  }, [patients, searchInput, searchCategory]);

  return (
    <div className={styles.container} ref={menuRef}>
      <label className={styles.entry_label} htmlFor='name'>
        {t('link_patient')} {disablePatientChange && <span className={styles.locked_indicator}>(Locked)</span>}
      </label>
      <div className={`${styles.op_selector_container} ${opMenu ? styles.active_menu : ''}`}>
        <input
          type='text'
          id='pt-name'
          name='pt-name'
          defaultValue={selectedPatient?.full_name || ''}
          placeholder={t('select_patient')}
          className={`${styles.selector_input} ${disablePatientChange ? styles.locked_field : ''}`}
          autoComplete='off'
          onClick={() => !disablePatientChange && setOpMenu(!opMenu)}
          disabled={disablePatientChange || (editing && selectedPatient)}
          readOnly={disablePatientChange}
        />
        <div className={styles.op_menu_container}>
          <div className={styles.op_menu_search}>
            {isNew && !disablePatientChange && (
              <button className={`${styles.op_category_search_btn}`} type='button' onClick={addNewPatientToggle}>
                <AddIcon className={styles.btn_icon_add} />
              </button>
            )}
            <input
              className={styles.op_menu_search_input}
              value={searchInput}
              placeholder={t('search')}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {/* Hide category toggle buttons if selectedCategory is set */}
            {!selectedCategory && (
              <>
                <button
                  className={`${styles.op_category_search_btn} ${searchCategory.includes('stomatology') ? styles.active : ''}`}
                  type='button'
                  onClick={() => setSearchCategory(searchCategory.includes('stomatology') ? searchCategory.filter(cat => cat !== 'stomatology') : [...searchCategory, 'stomatology'])}
                >
                  <StomatologyIcon className={styles.btn_icon_sto} />
                </button>
                <button
                  className={`${styles.op_category_search_btn} ${searchCategory.includes('pulmonology') ? styles.active : ''}`}
                  type='button'
                  onClick={() => setSearchCategory(searchCategory.includes('pulmonology') ? searchCategory.filter(cat => cat !== 'pulmonology') : [...searchCategory, 'pulmonology'])}
                >
                  <PulmonologyIcon className={styles.btn_icon_pul} />
                </button>
              </>
            )}
          </div>
          <ul className={styles.op_menu_list_container}>
            {filteredOperations?.length ? (
              filteredOperations?.map((pt) => (
                <li
                  key={pt.id}
                  className={styles.op_menu_item}
                  onClick={() => {
                    setSelectedPatient(pt)
                    setOpMenu(false)
                  }}
                >
                  <span className={styles.op_menu_text}>{pt.full_name}</span>
                </li>
              ))
            ) : (
              <li className={`${styles.op_menu_item} ${styles.disabled_entry}`} disabled>
                <span className={styles.op_menu_text} style={{ textAlign: 'center', width: '100%' }}>
                  {t('no_patients')}
                </span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  )
}
